import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NotificationBell } from "./components/notifications/NotificationCenter";
import { CommandPaletteTrigger } from "./components/command/CommandPalette";
import MessagesTrigger from "./components/messages/MessagesTrigger";

// Asset URLs
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_f4dcf876-a54b-46ca-8e9d-db4647d8c947/artifacts/ygiw5ti9_Asset%2011-8.png";
const HERO_BG =
  "https://customer-assets.emergentagent.com/job_f4dcf876-a54b-46ca-8e9d-db4647d8c947/artifacts/nruri0qk_oyuster%20hero.webp"; // hero background
const IMG_PLATFORM =
  "https://customer-assets.emergentagent.com/job_work-finder-5/artifacts/zjj9d8wx_Depositphotos_319747248_S.jpg"; // full-featured platform
const IMG_STEPS =
  "https://customer-assets.emergentagent.com/job_work-finder-5/artifacts/eq78vri9_Depositphotos_563146892_S.jpg"; // 3 steps
const IMG_INDEPENDENT =
  "https://customer-assets.emergentagent.com/job_work-finder-5/artifacts/oqqng85r_Depositphotos_182574176_S.jpg"; // independent

// Enhanced Header Component with Black Brand Theme
export const EnhancedHeader = ({
  isLoggedIn,
  userType,
  user,
  onLoginClick,
  onSignupClick,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSearchMenu, setShowSearchMenu] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY === 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLinkClick = (event) => {
    setShowProfileMenu(false);
  };

  return (
    <div
      className={`sticky top-0 z-50 px-4 pt-4 transition-colors duration-300 ${
        isAtTop ? "bg-cream-100/[0.82]" : "bg-transparent"
      }`}
    >
      <header className="mx-auto flex max-w-[1120px] items-center justify-between gap-4 rounded-full border-[1.5px] border-ink-v2 bg-cream-100/[0.82] py-2.5 pl-[22px] pr-3 shadow-[0_2px_6px_rgba(26,26,26,0.06)] backdrop-blur-xl">
        {/* Left cluster — wordmark + PRO badge, then the nav */}
        <div className="flex items-center gap-7">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/brand/logo-wordmark-black.png"
              alt="CareerBuddy"
              className="h-5 w-auto object-contain"
            />
            <span className="rounded-full border-[1.5px] border-ink-v2 bg-lime-300 px-2 py-1 text-[13px] font-extrabold uppercase leading-none tracking-[0.08em] text-ink-v2">
              pro
            </span>
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden items-center gap-6 lg:flex">
            <div className="group relative cursor-pointer py-2.5">
              <button
                type="button"
                className="flex items-center gap-1.5 text-sm font-semibold leading-none transition-colors hover:text-coral-500"
              >
                Find talent
                <span
                  aria-hidden="true"
                  className="text-[10px] transition-transform duration-120 group-hover:rotate-180 group-focus-within:rotate-180"
                >
                  &#9660;
                </span>
              </button>
              <div className="absolute -left-4 top-full z-[60] hidden pt-3.5 group-hover:block group-focus-within:block">
                <div className="flex min-w-[230px] flex-col gap-0.5 rounded-2xl border-[1.5px] border-ink-v2 bg-white p-2.5 shadow-pop">
                  <Link
                    to="/talent"
                    className="block rounded-[10px] px-3.5 py-[11px] hover:bg-[#FFF3EF]"
                  >
                    <span className="block text-sm font-bold leading-[1.2] text-ink-v2">
                      Browse the marketplace
                    </span>
                    <span className="mt-[3px] block text-xs leading-[1.4] text-warm-500">
                      Vetted profiles, real rates
                    </span>
                  </Link>
                  <Link
                    to="/manage-embedded"
                    className="block rounded-[10px] px-3.5 py-[11px] hover:bg-[#FFF3EF]"
                  >
                    <span className="block text-sm font-bold leading-[1.2] text-ink-v2">
                      Managed · Embedded
                    </span>
                    <span className="mt-[3px] block text-xs leading-[1.4] text-warm-500">
                      Brief us, we run the job
                    </span>
                  </Link>
                  <Link
                    to="/find-talent"
                    className="block rounded-[10px] px-3.5 py-[11px] hover:bg-[#FFF3EF]"
                  >
                    <span className="block text-sm font-bold leading-[1.2] text-ink-v2">
                      Categories
                    </span>
                    <span className="mt-[3px] block text-xs leading-[1.4] text-warm-500">
                      SDRs, RevOps, GTM & more
                    </span>
                  </Link>
                  <Link
                    to="/post-job"
                    className="block rounded-[10px] px-3.5 py-[11px] hover:bg-[#FFF3EF]"
                  >
                    <span className="block text-sm font-bold leading-[1.2] text-ink-v2">
                      Post a job
                    </span>
                    <span className="mt-[3px] block text-xs leading-[1.4] text-warm-500">
                      Scope your first engagement
                    </span>
                  </Link>
                </div>
              </div>
            </div>

            <div className="group relative cursor-pointer py-2.5">
              <button
                type="button"
                className="flex items-center gap-1.5 text-sm font-semibold leading-none transition-colors hover:text-coral-500"
              >
                Find work
                <span
                  aria-hidden="true"
                  className="text-[10px] transition-transform duration-120 group-hover:rotate-180 group-focus-within:rotate-180"
                >
                  &#9660;
                </span>
              </button>
              <div className="absolute -left-4 top-full z-[60] hidden pt-3.5 group-hover:block group-focus-within:block">
                <div className="flex min-w-[230px] flex-col gap-0.5 rounded-2xl border-[1.5px] border-ink-v2 bg-white p-2.5 shadow-pop">
                  <Link
                    to="/join-as-talent"
                    className="block rounded-[10px] px-3.5 py-[11px] hover:bg-[#FFF3EF]"
                  >
                    <span className="block text-sm font-bold leading-[1.2] text-ink-v2">
                      Apply to join
                    </span>
                    <span className="mt-[3px] block text-xs leading-[1.4] text-warm-500">
                      Top 5% only — prove it
                    </span>
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="block rounded-[10px] px-3.5 py-[11px] hover:bg-[#FFF3EF]"
                  >
                    <span className="block text-sm font-bold leading-[1.2] text-ink-v2">
                      How vetting works
                    </span>
                    <span className="mt-[3px] block text-xs leading-[1.4] text-warm-500">
                      Three gates, no shortcuts
                    </span>
                  </Link>
                  <Link
                    to="/jobs"
                    className="block rounded-[10px] px-3.5 py-[11px] hover:bg-[#FFF3EF]"
                  >
                    <span className="block text-sm font-bold leading-[1.2] text-ink-v2">
                      Browse jobs
                    </span>
                    <span className="mt-[3px] block text-xs leading-[1.4] text-warm-500">
                      Updated weekly
                    </span>
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/about"
              className="text-sm font-semibold leading-none text-ink-v2 transition-colors hover:text-coral-500"
            >
              Why CareerBuddy Pro
            </Link>

            <Link
              to="/blog"
              className="text-sm font-semibold leading-none text-ink-v2 transition-colors hover:text-coral-500"
            >
              Blog
            </Link>
          </nav>
        </div>

        {/* Right cluster — auth, profile and the utility triggers */}
        <div className="flex items-center gap-3">
          {/* Desktop-only items */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Global command palette (Cmd+K) trigger */}
            <CommandPaletteTrigger />
            {/* Notification center & messages — only when logged in */}
            {isLoggedIn && <MessagesTrigger />}
            {isLoggedIn && <NotificationBell />}
            {!isLoggedIn ? (
              <>
                <Link
                  to="/login"
                  className="hidden text-sm font-semibold leading-none text-ink-v2 transition-colors hover:text-coral-500 min-[600px]:block"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  className="rounded-full border-[1.5px] border-ink-v2 bg-coral-500 px-5 py-3 text-sm font-bold leading-none text-white shadow-pop-sm transition-all duration-120 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-x-px hover:-translate-y-0.5 hover:text-white hover:shadow-pop active:translate-x-0.5 active:translate-y-[3px] active:shadow-none"
                >
                  Sign up &rarr;
                </Link>
              </>
            ) : (
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 text-sm font-semibold leading-none transition-colors hover:text-coral-500"
                >
                  <div className="w-9 h-9 rounded-full border-[1.5px] border-ink-v2 bg-coral-500 text-white flex items-center justify-center font-bold text-sm">
                    {userType === "client"
                      ? "C"
                      : userType == "admin"
                        ? "A"
                        : "F"}
                  </div>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {showProfileMenu && (
                  <div className="absolute right-0 z-[60] mt-3.5 w-56 rounded-2xl border-[1.5px] border-ink-v2 bg-white p-2.5 shadow-pop">
                    {userType === "client" ? (
                      <>
                        <Link
                          to="/settings"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          👤 My Profile
                        </Link>
                        <Link
                          to="/post-job"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          ➕ Post a Job
                        </Link>
                        <Link
                          to="/my-jobs"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          📋 My Jobs
                        </Link>
                        {/* <Link
                          to="/manage-project"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          📊 My Projects
                        </Link> */}
                        <Link
                          to="/team-workspaces"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          📊 Workspaces
                        </Link>
                        <Link
                          to="/proposals"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          📝 Proposals
                        </Link>
                        <Link
                          to="/contracts"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          📋 Contracts
                        </Link>
                        <Link
                          to="/wallet"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          💰 My Wallet
                        </Link>
                        <Link
                          to="/messages"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          💬 Messages
                        </Link>
                        <div className="my-1.5 border-t border-warm-200"></div>
                        <Link
                          to="/security"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          🔒 Security
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/settings"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          👤 My Profile
                        </Link>
                        <Link
                          to="/jobs"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          🔍 Find Work
                        </Link>
                        <Link
                          to="/portfolio"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          📁 Portfolio
                        </Link>
                        <Link
                          to="/contracts"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          📋 Contracts
                        </Link>
                        <Link
                          to="/proposals"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          📝 Proposals
                        </Link>
                        <Link
                          to="/wallet"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          💰 My Wallet
                        </Link>
                        <Link
                          to="/messages"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          💬 Messages
                        </Link>
                        <div className="my-1.5 border-t border-warm-200"></div>
                        <Link
                          to="/verification"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          ✅ Get Verified
                        </Link>
                        <Link
                          to="/security"
                          onClick={handleLinkClick}
                          className="block rounded-[10px] px-3.5 py-2.5 text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                        >
                          🔒 Security
                        </Link>
                      </>
                    )}
                    <div className="my-1.5 border-t border-warm-200"></div>
                    <button
                      onClick={() => {
                        onLogout();
                      }}
                      className="block w-full rounded-[10px] px-3.5 py-2.5 text-left text-sm font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                    >
                      🚪 Log out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Search & Menu */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Mobile Search Button */}
            <button
              onClick={() => setShowSearchMenu(!showSearchMenu)}
              aria-label="Search"
              className="flex h-10 w-10 items-center justify-center rounded-full border-[1.5px] border-ink-v2 bg-white transition-colors hover:bg-[#FFF3EF]"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>

            {/* Mobile Menu Button */}
            <button
              aria-label="Menu"
              className="flex h-10 w-10 items-center justify-center rounded-full border-[1.5px] border-ink-v2 bg-white transition-colors hover:bg-[#FFF3EF]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Search Menu */}
      {showSearchMenu && (
        <div className="lg:hidden mx-auto mt-3 max-w-[1120px] rounded-3xl border-[1.5px] border-ink-v2 bg-white p-4 shadow-pop">
          <div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-full border-[1.5px] border-ink-v2 py-3 pl-10 pr-4 text-sm font-medium outline-none placeholder:text-warm-500"
              />
              <svg
                className="absolute left-3.5 top-3.5 w-5 h-5 text-warm-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden mx-auto mt-3 max-w-[1120px] rounded-3xl border-[1.5px] border-ink-v2 bg-white p-4 shadow-pop">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
            >
              Home
            </Link>
            <Link
              to="/services"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
            >
              Services
            </Link>
            <Link
              to="/jobs"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
            >
              Find Work
            </Link>
            <Link
              to="/talent"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
            >
              Find Talent
            </Link>
            <Link
              to="/for-companies"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
            >
              For Companies
            </Link>
            <Link
              to="/for-agencies"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
            >
              For Agencies
            </Link>
            <Link
              to="/post-job"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
            >
              Post a Job
            </Link>
            <div className="my-2 border-t border-warm-200"></div>
            {isLoggedIn ? (
              <>
                <Link
                  to="/settings"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                >
                  My Profile
                </Link>
                {userType === "client" && (
                  <Link
                    to="/my-jobs"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                  >
                    My Jobs
                  </Link>
                )}
                <Link
                  to="/proposals"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                >
                  Proposals
                </Link>
                <Link
                  to="/security"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                >
                  Security
                </Link>
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onLogout();
                  }}
                  className="block w-full rounded-[10px] px-3.5 py-2.5 text-left text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-ink-v2 hover:bg-[#FFF3EF]"
                >
                  Log In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="mt-1 block rounded-full border-[1.5px] border-ink-v2 bg-coral-500 px-5 py-3 text-center text-[15px] font-bold text-white shadow-pop-sm hover:text-white"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Scroll-reveal + Parallax hook (local to home page)
const useRevealAndParallax = () => {
  const containerRef = useRef(null);
  useEffect(() => {
    const root = containerRef.current || document;
    // Reveal on scroll
    const elements = Array.from(root.querySelectorAll(".reveal"));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.15 },
    );
    elements.forEach((el) => io.observe(el));

    // Parallax
    const parallaxEls = Array.from(root.querySelectorAll(".parallax"));
    const handleScroll = () => {
      const scrollY = window.scrollY || 0;
      parallaxEls.forEach((el) => {
        const depth = parseFloat(el.getAttribute("data-depth") || "0.2");
        el.style.transform = `translateY(${scrollY * depth * 0.2}px)`;
      });
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      elements.forEach((el) => io.unobserve(el));
      io.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  return containerRef;
};

// Import updated homepage
import { UpdatedHomePage } from "./updated-homepage";
import CBProHomePage from "./cbpro/HomePage";

// CareerBuddyPro v2 redesign — expressive, editorial, deel/oyster/contra-inspired.
// `UpdatedHomePage` is kept around for fallback / A-B comparison.
export const EnhancedHomePage = () => <CBProHomePage />;

export const OldEnhancedHomePage = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const testimonials = [
    {
      quote:
        "We hired a Senior React developer in 48 hours and shipped our MVP in 3 weeks.",
      author: "Lena Morgan",
      role: "VP Product, FintechCo",
      avatar: "https://i.pravatar.cc/150?img=5",
    },
    {
      quote:
        "The quality of talent is outstanding. We found AI experts who delivered beyond expectations.",
      author: "Sanjay Patel",
      role: "CTO, HealthAI",
      avatar: "https://i.pravatar.cc/150?img=12",
    },
    {
      quote: "Seamless payments and contracts. It just works.",
      author: "Maria Gonzales",
      role: "Founder, GrowthFlow",
      avatar: "https://i.pravatar.cc/150?img=32",
    },
  ];

  useEffect(() => {
    const t = setInterval(
      () => setCurrentTestimonial((i) => (i + 1) % testimonials.length),
      6000,
    );
    return () => clearInterval(t);
  }, []);

  const ref = useRevealAndParallax();

  return (
    <div className="text-white" ref={ref}>
      {/* Hero - Oyster Style */}
      <section className="relative bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center py-12 lg:py-20">
            {/* Left Content */}
            <div className="order-2 lg:order-1">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-6 text-gray-900">
                Hire Top Talent Today
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-xl">
                The best of the best in fractional marketing, design, and
                engineering. Available to hire in 24 hours.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/talent"
                  className="px-8 py-4 rounded-full font-semibold text-white transition-all hover:opacity-90 text-base sm:text-lg shadow-lg"
                  style={{ backgroundColor: "var(--primary)" }}
                >
                  Start Hiring →
                </Link>
                <Link
                  to="/register"
                  className="px-8 py-4 rounded-full font-semibold text-gray-900 bg-white border-2 border-gray-300 hover:border-gray-400 transition-all text-base sm:text-lg"
                >
                  Sign Up as Talent
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="order-1 lg:order-2">
              <img
                src={HERO_BG}
                alt="Top talent professionals"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Platform Feature */}
      <section className="py-16 sm:py-20 bg-black reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 font-heading">
                A full‑featured hiring platform
              </h2>
              <p className="text-gray-300 text-base sm:text-lg mb-6">
                We're with you on every step of the hiring journey—from job post
                to invoice. Our tools support you even after you've found the
                one.
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  "Job Posts",
                  "Candidates",
                  "Interviews",
                  "Assignments",
                  "Invoices",
                ].map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1 rounded-full bg-white/10 text-white border border-white/10"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <Link
                to="/post-job"
                className="inline-flex px-5 py-3 rounded-xl font-semibold transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: "var(--primary)",
                  color: "var(--primary-foreground)",
                }}
              >
                Generate a Job Post →
              </Link>
            </div>
            <div
              className="rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl parallax"
              data-depth="0.1"
            >
              <img
                src={IMG_PLATFORM}
                alt="Job Post Preview"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3 Steps */}
      <section className="py-16 sm:py-20 bg-gray-900/60 reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3
                className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 font-heading"
                style={{ color: "var(--primary)" }}
              >
                3 steps to the perfect match
              </h3>
              <p className="text-gray-300 text-base sm:text-lg mb-6">
                We've broken down the hiring process so the only decisions you
                have to make are the ones that matter.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {["Talent Expert", "Interview Candidates", "Sign Contract"].map(
                  (t) => (
                    <span
                      key={t}
                      className="px-3 py-1 rounded-full border"
                      style={{
                        backgroundColor: "rgba(26, 137, 23, 0.1)",
                        color: "var(--primary)",
                        borderColor: "rgba(26, 137, 23, 0.2)",
                      }}
                    >
                      {t}
                    </span>
                  ),
                )}
              </div>
              <div className="space-y-3 text-gray-300">
                <div className="flex gap-3 items-start">
                  <span className="mt-1">•</span>
                  <span>
                    Talk with a Talent Expert for a 15‑minute intake. We'll
                    clarify your goals and define the role.
                  </span>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="mt-1">•</span>
                  <span>
                    Interview a short‑list of candidates curated to your needs.
                  </span>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="mt-1">•</span>
                  <span>
                    Sign the contract and start building—payments and contracts
                    handled.
                  </span>
                </div>
              </div>
            </div>
            <div
              className="rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl parallax"
              data-depth="0.12"
            >
              <img
                src={IMG_STEPS}
                alt="Steps"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* For Freelancers */}
      <section className="py-16 sm:py-20 bg-black reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div
            className="rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl order-last lg:order-first parallax"
            data-depth="0.08"
          >
            <img
              src={IMG_INDEPENDENT}
              alt="Join as a freelancer"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 font-heading">
              Are you an independent?
            </h3>
            <p className="text-gray-300 text-base sm:text-lg mb-6 max-w-xl">
              Join our commission‑free marketplace with $30MM+ in projects. Keep
              100% of what you earn with 0% commission fees.
            </p>
            <Link
              to="/signup"
              className="px-6 py-3 rounded-2xl bg-primary text-black font-semibold hover:bg-primary"
            >
              Sign up as a freelancer
            </Link>
          </div>
        </div>
      </section>

      {/* Why CareerBuddyPro */}
      <section className="py-16 sm:py-20 bg-gray-900/60 reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-12 font-heading">
            Why CareerBuddyPro?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                t: "Quality Over Quantity",
                d: "We vet for technical skill and culture fit.",
              },
              {
                t: "Project‑First Approach",
                d: "We match based on outcomes, not just hourly rates.",
              },
              {
                t: "Modern Tech Focus",
                d: "AI/ML, modern web, mobile, and growth.",
              },
              {
                t: "Smooth Experience",
                d: "Search → interview → pay — handled end to end.",
              },
            ].map((c, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-2xl p-6"
              >
                <h4 className="text-xl font-semibold mb-2 font-heading">
                  {c.t}
                </h4>
                <p className="text-gray-300">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Categories (restored) */}
      <section className="py-16 sm:py-20 bg-black reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-8 font-heading">
            Explore talent categories
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                name: "Development & IT",
                skills: "2,100+ skills",
                icon: "💻",
                tags: ["React", "Python", "AI/ML"],
                link: "/services/web-development",
              },
              {
                name: "Design & Creative",
                skills: "1,200+ skills",
                icon: "🎨",
                tags: ["UI/UX", "Branding", "Video"],
                link: "/services/design",
              },
              {
                name: "Marketing & Sales",
                skills: "800+ skills",
                icon: "📈",
                tags: ["Digital Marketing", "SEO"],
                link: "/services/digital-marketing",
              },
              {
                name: "Writing & Content",
                skills: "600+ skills",
                icon: "✍️",
                tags: ["Copywriting", "Editing"],
                link: "/services/content-creation",
              },
              {
                name: "Business & Finance",
                skills: "400+ skills",
                icon: "💼",
                tags: ["Consulting", "Analysis"],
                link: "/services/business-consulting",
              },
              {
                name: "Data & Analytics",
                skills: "300+ skills",
                icon: "📊",
                tags: ["Data Science", "BI"],
                link: "/services/data-analytics",
              },
              {
                name: "Engineering",
                skills: "500+ skills",
                icon: "⚙️",
                tags: ["Mechanical", "Electrical"],
                link: "/services/technical-consulting",
              },
              {
                name: "Legal & Compliance",
                skills: "200+ skills",
                icon: "⚖️",
                tags: ["Contract Law", "IP Law"],
                link: "/talent",
              },
            ].map((cat, idx) => (
              <Link
                key={idx}
                to={cat.link}
                className="p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer transform hover:scale-105"
              >
                <div className="text-2xl sm:text-3xl mb-3">{cat.icon}</div>
                <div className="text-lg font-semibold font-heading">
                  {cat.name}
                </div>
                <div className="text-sm text-gray-300 mb-3">{cat.skills}</div>
                <div className="flex flex-wrap gap-2">
                  {cat.tags.map((t, i) => (
                    <span
                      key={i}
                      className="text-xs px-2 py-1 rounded-full bg-white/10 border border-white/10"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/talent"
              className="inline-flex px-6 py-3 rounded-xl bg-white text-black font-semibold hover:bg-opacity-90"
            >
              Browse All Categories
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials (restored) */}
      <section className="py-20 sm:py-24 bg-black relative overflow-hidden reveal">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-heading">
              Success stories
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-300">
              Join thousands who've transformed their work with our platform
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <blockquote className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-100 leading-relaxed mb-10 text-center">
              "{testimonials[currentTestimonial].quote}"
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <img
                src={testimonials[currentTestimonial].avatar}
                alt={testimonials[currentTestimonial].author}
                className="w-14 h-14 rounded-full border-4 border-white/20"
              />
              <div className="text-left">
                <div className="text-white font-semibold">
                  {testimonials[currentTestimonial].author}
                </div>
                <div className="text-gray-300 text-sm">
                  {testimonials[currentTestimonial].role}
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentTestimonial(i)}
                  className={`w-2.5 h-2.5 rounded-full ${i === currentTestimonial ? "bg-white" : "bg-white/40"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA (restored) */}
      <section className="py-16 sm:py-20 bg-gray-900/60 reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-6 font-heading">
            Ready to Build Something Amazing?
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-10 max-w-4xl mx-auto">
            Whether you're a company with a vision or a developer looking for
            your next challenge, CareerBuddyPro connects the right people for
            the right projects.
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl sm:text-2xl font-semibold mb-2 font-heading">
                For Companies
              </h3>
              <p className="text-gray-300 mb-4">
                Get matched with developers who deliver results, not just code.
              </p>
              <Link
                to="/post-job"
                className="inline-flex px-5 py-3 rounded-xl bg-white text-black font-semibold hover:bg-opacity-90"
              >
                Start Your Project
              </Link>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl sm:text-2xl font-semibold mb-2 font-heading">
                For Developers
              </h3>
              <p className="text-gray-300 mb-4">
                Work on projects that inspire you with teams that value your
                expertise.
              </p>
              <Link
                to="/signup"
                className="inline-flex px-5 py-3 rounded-xl bg-primary text-black font-semibold hover:bg-primary"
              >
                Join Our Network
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export const EnhancedFooter = () => {
  return (
    <footer className="bg-ink-v2 text-cream-100">
      <div className="mx-auto max-w-[1200px] px-6 pb-8 pt-16 lg:px-10">
        <div className="mb-14 flex flex-wrap items-start justify-between gap-8">
          <div className="flex max-w-[300px] flex-col gap-4">
            <div className="flex items-center gap-2">
              <img
                src="/brand/logo-wordmark-white.png"
                alt="CareerBuddy"
                className="h-5 w-auto object-contain"
              />
              <span className="rounded-full bg-lime-300 px-2 py-1 text-xs font-extrabold uppercase leading-none tracking-[0.08em] text-ink-v2">
                pro
              </span>
            </div>
            <p className="text-sm leading-[1.5] text-cream-100/60">
              The top 5% of African freelance talent &mdash; vetted by us,
              trusted with your job.
            </p>
          </div>
          <div className="flex flex-col items-start gap-2">
            <Link
              to="/signup"
              className="rounded-full border-[1.5px] border-ink-v2 bg-coral-500 px-7 py-4 text-[15px] font-bold leading-none text-white shadow-[4px_4px_0_#FFF7EE] transition-all duration-120 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-x-px hover:-translate-y-0.5 hover:text-white hover:shadow-[6px_6px_0_#FFF7EE] active:translate-x-0.5 active:translate-y-[3px] active:shadow-none"
            >
              Get Started &rarr;
            </Link>
            <span className="text-xs font-medium leading-[1.4] text-cream-100/50">
              Join thousands of professionals and companies
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* For Freelancers */}
          <div className="lg:col-span-1">
            <h3 className="mb-4 text-xs font-bold uppercase leading-none tracking-[0.12em] text-cream-100/50">
              For Freelancers
            </h3>
            <ul className="space-y-3">
              {/* <li>
                <Link
                  to="/freelancer-dashboard"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  CareerBuddyPro Premium
                </Link>
              </li> */}
              <li>
                <Link
                  to="/projects/manage"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Manage projects
                </Link>
              </li>
              <li>
                <Link
                  to="/verification"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Get verified as expert
                </Link>
              </li>
              <li>
                <Link
                  to="/jobs"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Find jobs
                </Link>
              </li>
              <li>
                <Link
                  to="/contracts"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Sign contracts
                </Link>
              </li>
              <li>
                <Link
                  to="/wallet"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Commission-free payments
                </Link>
              </li>
            </ul>
          </div>

          {/* For Companies */}
          <div className="lg:col-span-1">
            <h3 className="mb-4 text-xs font-bold uppercase leading-none tracking-[0.12em] text-cream-100/50">
              For Companies
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/for-companies"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  For companies
                </Link>
              </li>
              <li>
                <Link
                  to="/manage-team-projects"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Manage team projects
                </Link>
              </li>
              <li>
                <Link
                  to="/share-hiring-workspace"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Share hiring workspace
                </Link>
              </li>
              <li>
                <Link
                  to="/post-job"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Post jobs
                </Link>
              </li>
              <li>
                <Link
                  to="/talent"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Discover talent
                </Link>
              </li>
              <li>
                <Link
                  to="/sign-contracts"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Sign contracts
                </Link>
              </li>
              <li>
                <Link
                  to="/approve-invoices"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Approve invoices
                </Link>
              </li>
            </ul>
          </div>

          {/* Use Cases & Resources */}
          <div className="lg:col-span-1">
            <h3 className="mb-4 text-xs font-bold uppercase leading-none tracking-[0.12em] text-cream-100/50">
              Use Cases
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/for-companies"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  For companies
                </Link>
              </li>
              <li>
                <Link
                  to="/for-freelancers"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  For freelancers
                </Link>
              </li>
              <li>
                <Link
                  to="/for-agencies"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  For agencies
                </Link>
              </li>
              <li>
                <Link
                  to="/for-startups"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  For startups
                </Link>
              </li>
            </ul>

            <h3 className="mb-4 mt-10 text-xs font-bold uppercase leading-none tracking-[0.12em] text-cream-100/50">
              Resources
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/blog"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/partners"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Partner networks
                </Link>
              </li>
              {/* <li>
                <a
                  href="https://thecareerbuddy.com/blog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Blog & guides
                </a>
              </li> */}
              <li>
                <Link
                  to="/calculator"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Project cost calculator
                </Link>
              </li>
              <li>
                <Link
                  to="/hiring-guide"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Guide to remote hiring
                </Link>
              </li>
              <li>
                <Link
                  to="/freelancer-guide"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Freelancer success guide
                </Link>
              </li>
            </ul>
          </div>

          {/* Hire Talent by Category */}
          <div className="lg:col-span-2">
            <h3 className="mb-4 text-xs font-bold uppercase leading-none tracking-[0.12em] text-cream-100/50">
              Hire Talent
            </h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              <Link
                to="/talent"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Explore all
              </Link>
              <Link
                to="/talent/top"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Top professionals
              </Link>
              <Link
                to="/services/design"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Design
              </Link>
              <Link
                to="/services/web-development"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Engineering
              </Link>
              <Link
                to="/services/digital-marketing"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Marketing
              </Link>
              <Link
                to="/talent/music-audio"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Music & audio
              </Link>
              <Link
                to="/talent/social-media"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Social media
              </Link>
              <Link
                to="/talent/video-animation"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Video & animation
              </Link>
              <Link
                to="/talent/product"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Product
              </Link>
              <Link
                to="/services/business-consulting"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Business consulting
              </Link>
              <Link
                to="/services/content-creation"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Content creation
              </Link>
              <Link
                to="/services/data-analytics"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Data & analytics
              </Link>
              <Link
                to="/services/ai-development"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                AI & machine learning
              </Link>
              <Link
                to="/services/mobile-apps"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Mobile development
              </Link>
              <Link
                to="/talent/other"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Other specialties
              </Link>
              <Link
                to="/services/technical-consulting"
                className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
              >
                Technical consulting
              </Link>
            </div>
          </div>

          {/* Company Info */}
          <div className="lg:col-span-1">
            <h3 className="mb-4 text-xs font-bold uppercase leading-none tracking-[0.12em] text-cream-100/50">
              Company
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/about"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Mission
                </Link>
              </li>
              <li>
                <Link
                  to="/careers"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Careers
                </Link>
              </li>
              <li>
                <Link
                  to="/press"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Press
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/help"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Help center
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Terms & conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link
                  to="/security"
                  className="text-sm font-medium leading-[1.3] text-cream-100/85 transition-colors hover:text-coral-500"
                >
                  Security
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-6 border-t border-cream-100/15 pt-6">
          <span className="text-xs font-medium leading-[1.4] text-cream-100/50">
            &copy; 2026 CareerBuddy &middot; For Africa, By Africa.
          </span>
          <span className="text-xs font-medium leading-[1.4] text-cream-100/50">
            Lagos &middot; Nairobi &middot; Accra &middot; Johannesburg &middot;
            Cairo
          </span>
        </div>
      </div>
    </footer>
  );
};

export default {
  EnhancedHeader,
  EnhancedHomePage,
  EnhancedFooter,
};
